import React, {useEffect, useMemo, useState} from "react";
import {Grid, SxProps, Typography} from "@mui/material";
import {ISolicitacaoOrcamento} from "@models/solicitacoes/orcamento.ts";
import {useAlert} from "@contexts/hooks/useAlert.ts";
import IconStepper from "@shared/custom/IconStepper.tsx";
import {_OrcamentosEndpoints} from "@localization/siteMap/endpoints/_OrcamentosEndpoints.ts";
import {DefaultEventProps} from "@models/DefaultProps.ts";
import {ConfirmationComponent} from "@components/shared/ConfirmationComponent.tsx";
import {CurrencyExchange, SaveOutlined, ThumbDown, ThumbUp} from "@mui/icons-material";
import {FuncaoRecurso} from "@models/conta/funcaoRecurso.ts";
import {_RecursosEndpoints} from "@localization/siteMap/endpoints/_RecursosEndpoints.ts";
import {RejeitaOrcamentoComponent} from "@components/orcamento/RejeitaOrcamentoComponent.tsx";
import {ToMoney} from "@shared/MathHelp.ts";
import {useApi} from "@contexts/hooks/api/useApi.ts";
import {StepConfig} from "@shared/custom/StepOption.tsx";
import {useAuth} from "@contexts/hooks/UseAuth.tsx";
import {IPagination} from "@localization/siteMap/siteMapInterfaces.ts";
import {useSolicitacao} from "@components/solicitacoes/hooks/useSolicitacao.tsx";
import {useOrcamento} from "@components/orcamento/hooks/useOrcamento.ts";
import {useRecurso} from "@components/recurso/hooks/useRecurso.ts";
import {IRecursoDetalhesDto} from "@components/recurso/models/dto/IRecursoDetalhesDto.ts";
import {IFormaPagamento} from "@models/formaPagamento/IFormaPagamento.ts";
import {CreateAnexoOrcamentoComponent} from "@components/orcamento/CreateAnexoOrcamentoComponent.tsx";
import usePagination from "@components/shared/hooks/usePagination.tsx";

export interface OrcamentoStepsComponentProps {
	orcamento: ISolicitacaoOrcamento;
	sx?: SxProps
	events?: Pick<DefaultEventProps<ISolicitacaoOrcamento>, 'onUpdate'>
}

export function OrcamentoStepsComponent(props: OrcamentoStepsComponentProps) {
	const {createAlertFromResponse, addPopUp, closePopUp} = useAlert();
	const {confirmarOrcamento} = useSolicitacao()
	const {getDetalhes} = useRecurso()
	const {
		finalizarOrcamento,
		autorizarOrcamento,
		efetuaPagamento,
		getFormaPagamento,
		efetuaCompra,
		getArquivosOrcamento
	} = useOrcamento()
	const {Get} = useApi()
	const {orcamento, sx} = props;
	const {userData, hasAccess, permissoes} = useAuth()
	const [formaPagamento, setFormaPagamento] = useState<IFormaPagamento | undefined>()
	const [funcoesRecurso, setFuncoesRecurso] = useState<FuncaoRecurso[]>([]);
	const [recursoValores, setRecursoValores] = useState<IRecursoDetalhesDto | undefined>();
	const [pagination, setPagination] = useState<IPagination>({page: 1, pageSize: 1})
	const [arquivosPagination, setArquivosPagination] = usePagination()


	useEffect(() => {
		Get(_RecursosEndpoints.GetMinhasPermissoesRecurso(orcamento.solicitacao?.recursoId ?? 0)).then((x) => {
			if (x.hasData) {
				setFuncoesRecurso(x.items.map(r => r.nome))
			}
		})
	}, []);

	useEffect(() => {
		getDetalhes({data: {recursoId: props.orcamento.id}}).then(x => {
			if (x.items != null) {
				setRecursoValores(x.items)
			}
		})

		getFormaPagamento({idOrcamento: orcamento.id}).then(x => {
			setFormaPagamento(x.items)
		})
		getArquivosOrcamento({
			data: {idOrcamento: orcamento.id, tipos: ['OrcamentoComprovantePagamento']},
			pagination: arquivosPagination
		}).then(x => {
			setArquivosPagination(prev => ({...prev, ...x.pagination}))
		})
	}, [orcamento.id]);

	const isAntecipacao = useMemo(() => {
		return formaPagamento?.isAntecipacao ?? false
	}, [formaPagamento])

	const isCompravel = useMemo(() => {
		/** Verifica se a compra foi autorizada,
		 * Se o usuario confirmou,
		 * Se a forma de pagamento é antecipação e ainda não foi pago
		 * E se o usuario é o mesmo quem criou o orçamento, ou pelo menos possui permissão pra comprar de outras pessoas
		 * Retorna false caso alguma dessas condições não bata
		 * */
		if (orcamento.dataCompraEfetuada != null) return false;
		if (orcamento.dataCompraAutorizada == null) return false;
		if (orcamento.dataConfirmacaoSolicitante == null) return false;

		/* Obriga que seja pago antes de comprar */
		if (isAntecipacao) {
			if (orcamento.dataPagamentoEfetuado == null) return false;
		}
		if (orcamento.usuarioOrcamentoId != userData?.id) {
			/* Se o usuario não for o orçador e não tiver permissao para comprar de outras pessoas, retorna false*/
			if (!hasAccess('ComprarOrcamentoAll')) {
				return false;
			}
		}

		return true;
	}, [orcamento, formaPagamento, permissoes])

	const confirmUserStep = useMemo<StepConfig>(() => {
		return {
			label: 'Aguardando confirmação do solicitante ',
			fulfilledLabel: 'Solicitante confirmou orçamento',
			time: orcamento.dataConfirmacaoSolicitante,
			fulfilled: orcamento.dataConfirmacaoSolicitante != null,
			usuarioId: orcamento.usuarioConfirmacaoId,
			disabled: orcamento.solicitacao?.solicitanteId != userData?.id && !hasAccess('EditarSolicitacaoOutros'),
			variant: 'solid',
			onClick: () => {
				addPopUp({
					content: <ConfirmationComponent options={[{
						label: 'Confirmar orçamento',
						color: 'success',
						endIcon: <ThumbUp/>,
						onClick: () => {
							confirmarOrcamento({idOrcamento: orcamento.id}).then((x) => {
								createAlertFromResponse(x)
								if (x.status == 'Success' && props.events && props.events.onUpdate) {
									props.events.onUpdate(orcamento)
								}
							})
							closePopUp()
						}
					},
						{
							label: 'Rejeitar orçamento',
							color: 'error',
							endIcon: <ThumbDown/>,
							onClick: () => {
								closePopUp()
								addPopUp({
									content: <RejeitaOrcamentoComponent orcamentoId={orcamento.id}
									                                    onFinish={() => {
										                                    if (props.events != null && props.events.onUpdate != null) {
											                                    props.events.onUpdate(orcamento)
										                                    }
									                                    }}/>,
									title: 'Rejeitar orçamento',
									size: 'lg'
								})
							}
						}
						, {
							label: 'Cancelar',
							color: 'secondary',
							onClick: () => {
								closePopUp()
							}
						}]}><Grid>O orçamento está de acordo com sua solicitação? </Grid>

					</ConfirmationComponent>, title: 'Confirmar orçamento', size: 'lg'
				})

			},
		}
	}, [permissoes, orcamento, userData]);

	const onUpdate = props.events?.onUpdate;

	const confirmAuthorizeStep = useMemo<StepConfig>(() => {
		return {
			label: 'Aguardando autorização de compra',
			fulfilled: orcamento.dataCompraAutorizada != null,
			fulfilledLabel: 'Compra autorizada',
			usuarioId: orcamento.usuarioAutorizadorId,
			time: orcamento.dataCompraAutorizada,
			icon: orcamento.usuarioAutorizadorId != null && orcamento.usuarioAutorizadorId != userData?.id ?
				<ThumbUp/> : undefined,
			disabled: !hasAccess(['AprovarOrcamentoMembro']) && (!funcoesRecurso.includes('Aprovador') || !funcoesRecurso.includes('Diretor') || !funcoesRecurso.includes('Gestor')),
			onClick: () => {
				addPopUp({
					content: <ConfirmationComponent options={[{
						label: 'Autorizar compra',
						color: 'success',
						endIcon: <ThumbUp/>,
						hidden: recursoValores != null && recursoValores.valorDisponivel < orcamento.valorTotal,
						onClick: () => {
							setPagination(prev => ({...prev, loading: true}))
							autorizarOrcamento({idOrcamento: orcamento.id}).then((x) => {
								createAlertFromResponse(x)
								if (x.status == 'Success' && onUpdate != null) {
									onUpdate()
									closePopUp()
								}
							}).finally(() => {
								setPagination(prev => ({...prev, loading: false}))
							})
						}
					}, {
						label: 'Solicitar + recurso',
						color: 'info',
						hidden: recursoValores == null || recursoValores.valorDisponivel > orcamento.valorTotal,
						endIcon: <CurrencyExchange/>,
						onClick: () => {
							Get(_RecursosEndpoints.CreateSolicitacaoTransferenciaRecurso({
								orcamentoId: orcamento.id,
								valor: orcamento.valorTotal
							})).then(x => {
								createAlertFromResponse(x)
								if (onUpdate != null) onUpdate()
							})
						}
					}, {
						label: 'Rejeitar compra',
						color: 'error',
						endIcon: <ThumbDown/>,
						onClick: () => {
							closePopUp()
							addPopUp({
								content: <RejeitaOrcamentoComponent orcamentoId={orcamento.id}
								                                    onFinish={() => {
									                                    if (onUpdate != null) {
										                                    onUpdate(orcamento)
									                                    }
								                                    }}/>,
								title: 'Rejeitar orçamento',
								size: 'lg'
							})
						}
					}, {
						label: 'Cancelar',
						color: 'secondary',
						onClick: () => {
							closePopUp()
						}
					}
					]}><Grid>
						<Typography>{recursoValores != null && recursoValores.valorDisponivel < orcamento.valorTotal ? `É necessário mais ${ToMoney(orcamento.valorTotal - recursoValores.valorDisponivel)} de verba no recurso, deseja solicitar mais?` : "Você deseja autorizar a compra?"}</Typography>
					</Grid>
					</ConfirmationComponent>,
					size: 'lg',
					title: 'Autorizar a compra'
				})
			},
			variant: 'solid',
		}
	}, [permissoes, orcamento, recursoValores, userData])

	const payStep = useMemo<StepConfig>(() => {
		return {
			label: `Aguardando pagamento`,
			fulfilledLabel: 'Pagamento realizado',
			fulfilled: orcamento.dataPagamentoEfetuado != null,
			disabled: !hasAccess(['ConfirmarPagamento']) || orcamento.dataCompraAutorizada == null || orcamento.dataConfirmacaoSolicitante == null,
			time: orcamento.dataPagamentoEfetuado,
			usuarioId: orcamento.usuarioPagamentoId,
			onClick: () => {
				const handlePagamento = async () => {
					return await efetuaPagamento({idOrcamento: orcamento.id})
						.then((x) => {
							createAlertFromResponse(x)
							if (x.status == 'Success') {
								closePopUp()
								if (onUpdate != null) onUpdate()
							}
						})
						.finally(() => {
							setPagination(prev => ({...prev, loading: false}))
						})
				}
				addPopUp({
						content: <ConfirmationComponent options={[{
							label: 'Adicionar comprovante e confirmar',
							color: 'primary',
							onClick: () => {
								addPopUp({
									title: 'Adicionar comprovante e confirmar', content: <CreateAnexoOrcamentoComponent
										value={orcamento}
										tipo={'OrcamentoComprovantePagamento'}
										events={{
											onCreate: () => {
												handlePagamento().then(() => closePopUp())
											}
										}}/>
								})
							}
						},
							{
								label: 'Apenas confirmar',
								color: 'primary',
								disabled: arquivosPagination.total == 0,
								onClick: () => {
									setPagination(prev => ({...prev, loading: true}))
									efetuaPagamento({idOrcamento: orcamento.id}).then((x) => {
										createAlertFromResponse(x)
										if (x.status == 'Success') {
											if (onUpdate != null) onUpdate()
										}
									}).finally(() => {
										setPagination(prev => ({...prev, loading: false}))
									})
									closePopUp()
								}
							}, {
								label: 'Cancelar',
								color: 'error',
								onClick: () => {
									closePopUp()
								}
							}
						]}>
							<Grid>Deseja confirmar o pagamento do orçamento?</Grid>
						</ConfirmationComponent>,
						title: 'Confirmar pagamento',
					}
				)
			},
			variant: 'solid',
		}
	}, [permissoes, orcamento, userData]);

	const buyStep = useMemo<StepConfig>(() => {
		return {
			label: `Aguardando compra`,
			time: orcamento.dataCompraEfetuada,
			usuarioId: orcamento.usuarioCompradorId,
			onClick: () => {
				addPopUp({
					content: <ConfirmationComponent options={[{
						label: 'Confirmar compra',
						color: 'primary',
						onClick: () => {
							setPagination(prev => ({...prev, loading: true}))
							efetuaCompra({idOrcamento: orcamento.id}).then((x) => {
								createAlertFromResponse(x)
								if (x.status == 'Success' && onUpdate != null) {
									onUpdate(orcamento)
								}
							}).finally(() => {
								setPagination(prev => ({...prev, loading: false}))
							})
							closePopUp()
						},
						endIcon: <SaveOutlined/>
					}, {
						label: 'Cancelar',
						color: 'secondary',
						onClick: () => {
							closePopUp()
						}
					}]}><Grid>Deseja confirmar a compra do orçamento? </Grid>
					</ConfirmationComponent>, title: 'Confirmar compra', size: 'lg'
				})
			},
			disabled: !isCompravel,
			fulfilled: orcamento.dataCompraEfetuada != null,
			fulfilledLabel: 'Compra efetuada',
			variant: 'solid',
		}
	}, [permissoes, orcamento, userData])

	const receiveStep = useMemo<StepConfig>(() => {
		return {
			label: 'Aguardando entrega',
			usuarioId: orcamento.usuarioConfirmaEntregaId,
			onClick: () => {
				addPopUp({
					content: <ConfirmationComponent options={[{
						label: 'Confirmar entrega',
						color: 'primary',
						onClick: () => {
							setPagination(prev => ({...prev, loading: true}))

							Get(_OrcamentosEndpoints.ConfirmaEntregaOrcamento({orcamentoId: orcamento.id})).then((x) => {
								createAlertFromResponse(x)
								if (x.status == 'Success') {
									if (onUpdate != null) onUpdate()
								}
							}).finally(() => {
								setPagination(prev => ({...prev, loading: false}))
							})
							closePopUp()
						},
						endIcon: <ThumbUp/>
					}, {
						label: 'Cancelar',
						color: 'secondary',
						onClick: () => {
							closePopUp()
						}
					}]}><Grid>Deseja confirmar a entrega do produto? </Grid>
					</ConfirmationComponent>, title: 'Confirmar entrega', size: 'lg'
				})
			},
			time: orcamento.dataEntrega,
			disabled: orcamento.dataEntrega != null || orcamento.dataCompraEfetuada == null,
			fulfilledLabel: 'Produto entregue ao comprador',
			fulfilled: orcamento.dataEntrega != null,
			variant: 'solid',
		}
	}, [permissoes, orcamento, userData]);
	const finalizeStep = useMemo<StepConfig>(() => {
		return {
			label: 'Aguardando o solicitante finalizar',
			fulfilledLabel: 'Solicitante confirmou a finalização',
			fulfilled: orcamento.status == 'Finalizado',
			time: orcamento.dataFinalizacao,
			usuarioId: orcamento.usuarioFinalizacaoId,
			disabled: orcamento.status == 'Finalizado' || orcamento.dataEntrega == null || (orcamento.solicitacao?.solicitanteId !== userData?.id && !hasAccess('EditarSolicitacaoOutros')),
			onClick: () => {
				setPagination(prev => ({...prev, loading: true}))
				finalizarOrcamento({idOrcamento: orcamento.id}).then((x) => {
					if (x.status == 'Success') {
						if (onUpdate != null) onUpdate()
					}
					createAlertFromResponse(x)
				}).finally(() => {
					setPagination(prev => ({...prev, loading: false}))
				})
			},
			variant: 'solid',
		}
	}, [permissoes, orcamento, userData]);

	return (<Grid sx={{...sx}}>
		<Grid container sx={{overflow: 'auto'}}>
			<IconStepper sx={{mt: 1}}
			             disabled={props.orcamento.status == 'Rejeitado' || props.orcamento.status == 'Cancelado' || pagination.loading}
			             steps={[{
				             label: 'Orçamento Incluso ',
				             variant: 'solid',
				             time: orcamento.dataOrcamento,
				             usuarioId: orcamento.usuarioOrcamentoId,
				             fulfilled: true
			             }, confirmUserStep,
				             confirmAuthorizeStep,
				             payStep,
				             buyStep,
				             receiveStep,
				             finalizeStep
			             ]} size={'sm'}/>
		</Grid>
	</Grid>)
}